//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { useConfig } from 'contexts/ConfigProvider';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { BuildVersionList } from 'utils/helper';
import { sseConnectionManager } from 'utils/sseConnectionManager';
import { useActionProgressFlow } from './useActionProgressFlow';
import { EventTriggerSource, useSseFlashStatus } from './useSseFlashStatus';

interface HwTriggerData {
  action?: number;
}

const HwResetMessage = () => {
  const { t } = useTranslation();
  return (
    <>
      <span className="pb-4 font-bold italic text-toast-warning-800">{t('modal.systemResetViaDeviceBtn')} </span>
      <span>{t('modal.deviceResetNotificationMsg')}</span>
    </>
  );
};

export const useSseHwTrigger = () => {
  const { t } = useTranslation();
  const { cbtApiUrl, buildVersion } = useConfig();
  const location = useLocation();

  const unsubscribeRef = useRef<(() => void) | null>(null);

  const resetTime = 3 * 60 * 1000;

  const { displayArchivSystemRadialProgressModal } = useSseFlashStatus();
  const { runActionProgress } = useActionProgressFlow();

  const displayResetProgressModal = useCallback(
    () =>
      runActionProgress({
        checkId: 'reset',
        name: 'hwReset',
        title: t('modal.deviceResetNotification'),
        message: <HwResetMessage />,
        type: 'warning',
        totalTime: resetTime,
        redirectDelay: 1000,
      }),
    [runActionProgress, t, resetTime]
  );

  const handleHwTriggerData = useCallback(
    (data: HwTriggerData) => {
      if (/^(\/sites\/\d+)?\/rtty\//.test(location.pathname)) return;
      if (buildVersion !== BuildVersionList.controller) return;
      const { action } = data;

      if (action === 1) {
        /* 前端無法區分裝置按鈕與 LCM,兩者統一標記為 ResetButton(後端仍記錄真實來源) */
        displayArchivSystemRadialProgressModal({ isRestart: true, triggerSource: EventTriggerSource.ResetButton });
      } else if (action === 2) {
        displayResetProgressModal();
      }
    },
    [location.pathname, buildVersion, displayArchivSystemRadialProgressModal, displayResetProgressModal]
  );

  const closeSseHwTrigger = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
  }, []);

  const startSseHwTrigger = useCallback(() => {
    if (!cbtApiUrl) return;
    if (unsubscribeRef.current) return; // 已經訂閱中
    const baseUrl = cbtApiUrl.replace(/\/$/, '');
    const hwTriggerUrl = `${baseUrl}/api/sse/hwTrigger`;
    unsubscribeRef.current = sseConnectionManager.subscribe('HW_TRIGGER', hwTriggerUrl, handleHwTriggerData);
  }, [cbtApiUrl, handleHwTriggerData]);

  useEffect(() => {
    return () => closeSseHwTrigger();
  }, [closeSseHwTrigger]);

  return {
    startSseHwTrigger,
    closeSseHwTrigger,
  };
};
